import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { sfCartAPI } from '../services/storefront';

const CartContext = createContext();

/**
 * Cart strategy:
 *  - Guest:         X-Session-Token header (auto-added by storefront.js axios instance)
 *  - Authenticated: Authorization Bearer header (auto-added when customer token present)
 *  - On login:      AuthContext calls sfCartAPI.merge() — guest items absorbed into account cart
 *
 * Local state is the source of truth for rendering.
 * Every mutation hits the NexusCRM backend first, then syncs local state from the response.
 */

const LEGACY_CART_KEY = 'nagasboutique-cart';

// Normalise NexusCRM cart item fields to the flat shape Cart.jsx / Checkout.jsx expect.
// NexusCRM returns: { id, productId, variantId, quantity, unitPrice, lineTotal, product: { name, slug, imageUrl }, variant: { colorName } }
const normalizeCartItem = (item) => ({
  ...item,
  name:      item.product?.name     || item.productName || item.name,
  image:     item.variant?.imageUrl || item.product?.imageUrl || item.imageUrl || item.image || item.image_url,
  image_url: item.variant?.imageUrl || item.product?.imageUrl || item.imageUrl || item.image_url || item.image,
  price:     item.unitPrice          ?? item.price,
  slug:      item.product?.slug      || item.slug,
  color:     item.variant?.colorName || item.variant?.color_name || item.color,
});

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [offerMeta, setOfferMeta] = useState({ offerSubtotal: 0, offerShipping: 0, offerWarning: null, offerSummary: null });
  const [serverSubtotal, setServerSubtotal] = useState(null);

  // ── Fetch cart from backend ─────────────────────────────────────────────────
  const fetchCart = useCallback(async () => {
    try {
      setSyncing(true);
      const data = await sfCartAPI.get();
      setItems(Array.isArray(data?.items) ? data.items.map(normalizeCartItem) : []);
      setServerSubtotal(data?.subtotal ?? null);
      setOfferMeta({
        offerSubtotal: data?.offerSubtotal ?? 0,
        offerShipping: data?.offerShipping ?? 0,
        offerWarning: data?.offerWarning ?? null,
        offerSummary: data?.offerSummary ?? null,
      });
    } catch (err) {
      console.error('Cart fetch failed:', err);
      // Fall back to legacy localStorage cart so the page isn't blank
      const saved = localStorage.getItem(LEGACY_CART_KEY);
      if (saved) {
        try { setItems((JSON.parse(saved)?.items || []).map(normalizeCartItem)); } catch {}
      }
    } finally {
      setSyncing(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  // Re-fetch when customer token changes (login / logout)
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === 'storefront-customer-token') fetchCart();
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [fetchCart]);

  // ── Cart mutations ──────────────────────────────────────────────────────────

  const addToCart = async (product, variantId = null, quantity = 1) => {
    try {
      await sfCartAPI.addItem({ productId: product.id, variantId, quantity });
      await fetchCart();
      return { success: true };
    } catch (error) {
      console.error('addToCart failed:', error);
      return { success: false, error: error.response?.data?.error || error.message };
    }
  };

  const removeFromCart = async (cartItemId) => {
    // Optimistic removal
    setItems((prev) => prev.filter((i) => i.id !== cartItemId));
    try {
      await sfCartAPI.removeItem(cartItemId);
      await fetchCart();
      return { success: true };
    } catch (error) {
      console.error('removeFromCart failed:', error);
      await fetchCart(); // re-sync on error
      return { success: false, error: error.response?.data?.error || error.message };
    }
  };

  const updateQuantity = async (cartItemId, quantity) => {
    if (quantity < 0) return { success: false, error: 'Invalid quantity' };
    if (quantity === 0) return removeFromCart(cartItemId);

    // Optimistic update
    setItems((prev) =>
      prev.map((i) => (i.id === cartItemId ? { ...i, quantity } : i))
    );
    try {
      await sfCartAPI.updateItem(cartItemId, quantity);
      await fetchCart();
      return { success: true };
    } catch (error) {
      console.error('updateQuantity failed:', error);
      await fetchCart();
      return { success: false, error: error.response?.data?.error || error.message };
    }
  };

  const clearCart = async () => {
    setItems([]);
    try {
      await sfCartAPI.clear();
      return { success: true };
    } catch (error) {
      console.error('clearCart failed:', error);
      await fetchCart();
      return { success: false, error: error.response?.data?.error || error.message };
    }
  };

  // ── Derived values ──────────────────────────────────────────────────────────

  const getCartTotal = () =>
    items.reduce((total, item) => {
      // NexusCRM cart items have `unitPrice` and `lineTotal`; fall back to `price` for legacy items
      if (item.lineTotal != null) return total + item.lineTotal;
      if (item.unitPrice != null) return total + item.unitPrice * (item.quantity || 1);
      const price =
        typeof item.price === 'string'
          ? parseFloat(item.price.replace(/[^0-9.]/g, ''))
          : parseFloat(item.price || 0);
      return total + price * (item.quantity || 1);
    }, 0);

  const getCartItemsCount = () =>
    items.reduce((total, item) => total + (item.quantity || 1), 0);

  return (
    <CartContext.Provider
      value={{
        items,
        isOpen,
        setIsOpen,
        syncing,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        fetchCart,
        getCartTotal,
        getCartItemsCount,
        offerMeta,
        serverSubtotal,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) throw new Error('useCart must be used within a CartProvider');
  return context;
};
